















import { defineComponent } from "@vue/composition-api";
import { PaymentPackage } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { checkAuthorities } from "@scrile/tools/dist/lib/SecurityHelpers";
import { $t } from "@/lib/i18nHelpers";
import { printPaymentPackage } from "@/lib/moneyHelpers";
import config from "@/config";
import { FormFields, FormValues, ListFields } from "@/types";
import BaseButton from "@/components/BaseButton";
import BaseForm from "@/components/BaseForm";
import BasePage from "@/components/BasePage";
import PageTitle from "@/components/PageTitle";

export default defineComponent({
  name: "PriceSettingsView",
  components: { BasePage, PageTitle, BaseForm, BaseButton },
  props: {
    loading: { type: Boolean, required: true },
    callPaymentPackages: { type: Array as () => PaymentPackage[], required: true },
    messagePaymentPackages: { type: Array as () => PaymentPackage[], required: true },
    values: { type: Object as () => FormValues, required: true },
  },
  model: {
    event: "input",
    prop: "values",
  },
  data() {
    return {
      saving: false as boolean,
    };
  },
  computed: {
    checkedCallOptionsAuthorities(): boolean {
      return checkAuthorities(this.$store.state?.authorities || [], ["updateMyCallOptions"]);
    },
    checkedCallAuthorities(): boolean {
      return checkAuthorities(this.$store.state?.authorities || [], ["updateMyCallPrice"]);
    },
    checkedMessageAuthorities(): boolean {
      return checkAuthorities(this.$store.state?.authorities || [], ["updateMyMessagePrice"]);
    },
    perMinuteCallPrices(): ListFields {
      return [
        { value: "0", text: $t("Free") },
        ...this.callPaymentPackages.map(item => ({ value: item.id, text: printPaymentPackage(item) })),
      ];
    },

    perSentMessagePrices(): ListFields {
      return [
        { value: "0", text: $t("Free") },
        ...this.messagePaymentPackages.map(item => ({ value: item.id, text: printPaymentPackage(item) })),
      ];
    },
    fields(): FormFields {
      const fields: FormFields = [];
      if (config.featureCalling) {
        if (this.checkedCallOptionsAuthorities) {
          fields.push(
            {
              name: "title__calls",
              type: "title",
              label: $t("Calls"),
              class: "title",
            },
            {
              class: "__call-enabling",
              name: "incomingCallsEnabled",
              label: $t("Enable incoming calls"),
              type: "switch",
            }
          );
        }
        if ((this.values.incomingCallsEnabled || !this.checkedCallOptionsAuthorities) && this.checkedCallAuthorities) {
          fields.push({
            class: "__call-price",
            name: "callPricePackageId",
            label: $t("Minute of call"),
            type: "select",
            options: this.perMinuteCallPrices,
          });
        }
      }
      if (config.featurePaidSentMessage && this.checkedMessageAuthorities) {
        fields.push(
          {
            name: "title__text-messages",
            type: "title",
            label: $t("Text messages"),
            class: "title __top-border",
          },
          {
            class: "__per-message",
            name: "perSentPriceId",
            label: $t("For non-subscribers"),
            type: "select",
            options: this.perSentMessagePrices,
          },
          {
            class: "__per-message-subscriber",
            name: "subscriberPerSentPriceId",
            label: $t("For subscribers"),
            type: "select",
            options: this.perSentMessagePrices,
          },
          {
            name: "title__media-messages",
            type: "title",
            label: $t("Media messages"),
            class: "title __top-border",
          },
          {
            class: "__per-media-message",
            name: "perSentWithMediaPriceId",
            label: $t("For non-subscribers"),
            type: "select",
            options: this.perSentMessagePrices,
          },
          {
            class: "__per-media-message-subscriber",
            name: "subscriberPerSentWithMediaPriceId",
            label: $t("For subscribers"),
            type: "select",
            options: this.perSentMessagePrices,
          },
          ...(config.featureAudioMessageAttachments
            ? [
                {
                  name: "title__audio-messages",
                  type: "title",
                  label: $t("Audio messages"),
                  class: "title __top-border",
                },
                {
                  class: "__per-audio-message",
                  name: "perSentWithAudioPriceId",
                  label: $t("For non-subscribers"),
                  type: "select",
                  options: this.perSentMessagePrices,
                },
                {
                  class: "__per-audio-message-subscriber",
                  name: "subscriberPerSentWithAudioPriceId",
                  label: $t("For subscribers"),
                  type: "select",
                  options: this.perSentMessagePrices,
                },
              ]
            : [])
        );
      }
      return fields;
    },
  },
  methods: {
    onInput(values: FormValues) {
      const isPerSentValueChanged = this.values.perSentPriceId !== values.perSentPriceId;
      const isPerSentWithMediaValueChanged = this.values.perSentWithMediaPriceId !== values.perSentWithMediaPriceId;
      const isPerSentWithAudioValueChanged = this.values.perSentWithAudioPriceId !== values.perSentWithAudioPriceId;
      const isSubscriberPerSentValueChanged = this.values.subscriberPerSentPriceId !== values.subscriberPerSentPriceId;
      const isSubscriberPerSentWithMediaValueChanged =
        this.values.subscriberPerSentWithMediaPriceId !== values.subscriberPerSentWithMediaPriceId;
      const isSubscriberPerSentWithAudioValueChanged =
        this.values.subscriberPerSentWithAudioPriceId !== values.subscriberPerSentWithAudioPriceId;

      const textMessagePrice = this.messagePaymentPackages.find(p => p.id === values.perSentPriceId)?.amount ?? 0;
      const mediaMessagePrice =
        this.messagePaymentPackages.find(p => p.id === values.perSentWithMediaPriceId)?.amount ?? 0;
      const audioMessagePrice =
        this.messagePaymentPackages.find(p => p.id === values.perSentWithAudioPriceId)?.amount ?? 0;
      const subscriberTextMessagePrice =
        this.messagePaymentPackages.find(p => p.id === values.subscriberPerSentPriceId)?.amount ?? 0;
      const subscriberMediaMessagePrice =
        this.messagePaymentPackages.find(p => p.id === values.subscriberPerSentWithMediaPriceId)?.amount ?? 0;
      const subscriberAudioMessagePrice =
        this.messagePaymentPackages.find(p => p.id === values.subscriberPerSentWithAudioPriceId)?.amount ?? 0;

      if (isPerSentValueChanged) {
        if (textMessagePrice > mediaMessagePrice) {
          values.perSentWithMediaPriceId = values.perSentPriceId;
        }
        if (textMessagePrice > audioMessagePrice) {
          values.perSentWithAudioPriceId = values.perSentPriceId;
        }
      } else if (isPerSentWithMediaValueChanged && mediaMessagePrice < textMessagePrice) {
        values.perSentPriceId = values.perSentWithMediaPriceId;
      } else if (isPerSentWithAudioValueChanged && audioMessagePrice < textMessagePrice) {
        values.perSentPriceId = values.perSentWithAudioPriceId;
      } else if (isSubscriberPerSentValueChanged) {
        if (subscriberTextMessagePrice > subscriberMediaMessagePrice) {
          values.subscriberPerSentWithMediaPriceId = values.subscriberPerSentPriceId;
        }
        if (subscriberTextMessagePrice > subscriberAudioMessagePrice) {
          values.subscriberPerSentWithAudioPriceId = values.subscriberPerSentPriceId;
        }
      } else if (isSubscriberPerSentWithMediaValueChanged && subscriberMediaMessagePrice < subscriberTextMessagePrice) {
        values.subscriberPerSentPriceId = values.subscriberPerSentWithMediaPriceId;
      } else if (isSubscriberPerSentWithAudioValueChanged && subscriberAudioMessagePrice < subscriberTextMessagePrice) {
        values.subscriberPerSentPriceId = values.subscriberPerSentWithAudioPriceId;
      }
      this.$emit("input", values);
    },
    onSave() {
      this.saving = true;
      this.$emit("submit", () => (this.saving = false));
    },
  },
});
